'use client';

/**
 * Shared scaffolding for third-party embed blocks (Calendly, Typeform,
 * HubSpot, EmbeddedVideo). Card wrapper + loading affordance + error
 * fallback + stable id + a small client-side script loader (no
 * {@code next/script} dep so the package stays Next-agnostic).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useBlockRootAttrs, useBlockControlStamp } from '@akademi/site-builder-runtime';

export function EmbedCard({
  title,
  accent,
  children,
}: {
  title?: string;
  /** Optional accent icon shown next to the title. */
  accent?: ReactNode;
  children: ReactNode;
}) {
  // This <section> is the block root for every embed block (Calendly,
  // Typeform, HubSpot*, …) — carry the data-<ns>-* identity the renderer
  // put in context. See blockIdentity.tsx.
  const rootAttrs = useBlockRootAttrs();
  const rootRef = useBlockControlStamp<HTMLElement>();
  return (
    <section
      {...rootAttrs}
      ref={rootRef}
      style={{
        background: 'var(--color-surface-card, #fff)',
        color: 'var(--color-text-primary, #111113)',
        border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
        borderRadius: 'var(--radius-lg, 12px)',
        padding: 'clamp(16px, 4vw, 24px)',
        maxWidth: 800,
        margin: '0 auto',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {(title || accent) && (
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 16,
          }}
        >
          {accent}
          {title && (
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-lg, 18px)',
                fontWeight: 700,
                lineHeight: 1.25,
                color: 'var(--color-text-primary, #111113)',
              }}
            >
              {title}
            </h2>
          )}
        </header>
      )}
      {children}
    </section>
  );
}

export function LoadingState({ label }: { label: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        padding: 'var(--space-10, 40px) var(--space-4, 16px)',
        textAlign: 'center',
        fontSize: '13px',
        color: 'var(--color-text-tertiary, #71717a)',
      }}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 18,
          height: 18,
          marginRight: 8,
          marginBottom: -3,
          borderRadius: '50%',
          border: '2px solid var(--color-border-subtle, rgba(0,0,0,0.12))',
          borderTopColor: 'var(--color-brand-500, #4f46e5)',
          animation: 'lms-eb-spin 0.8s linear infinite',
        }}
      />
      {label}
      <style>{`@keyframes lms-eb-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export function ConfigurationError({ message }: { message: string }) {
  return (
    <EmbedCard>
      <div
        role="alert"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: 'var(--space-4, 16px) var(--space-5, 20px)',
          borderRadius: 'var(--radius-md, 8px)',
          background: 'var(--color-surface-muted, rgba(220,38,38,0.06))',
          color: 'var(--color-text-primary, #111113)',
          border: '1px solid var(--color-border-subtle, rgba(220,38,38,0.18))',
        }}
      >
        <AlertTriangleIcon />
        <span style={{ fontSize: 'var(--font-size-sm, 14px)', lineHeight: 1.5 }}>{message}</span>
      </div>
    </EmbedCard>
  );
}

export function useStableId(prefix: string): string {
  const ref = useRef<string | null>(null);
  if (ref.current === null) {
    ref.current = `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return ref.current;
}

/**
 * Inject an external script tag on mount; idempotent (dedupes by src).
 * Replacement for {@code next/script} that doesn't pull a Next dep into
 * the shared package. Returns {@code true} once the script has loaded.
 */
export function useExternalScript(src: string | null): boolean {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!src || typeof document === 'undefined') return;
    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    if (existing) {
      // Already loaded or in-flight.
      if (existing.dataset.loaded === 'true') {
        setLoaded(true);
      } else {
        existing.addEventListener('load', () => setLoaded(true), { once: true });
      }
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.addEventListener(
      'load',
      () => {
        s.dataset.loaded = 'true';
        setLoaded(true);
      },
      { once: true },
    );
    document.body.appendChild(s);
  }, [src]);
  return loaded;
}

function AlertTriangleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-feedback-error, #dc2626)"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ flexShrink: 0, marginTop: 1 }}
    >
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

export function CalendarIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-brand-500, #4f46e5)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

export function ClipboardIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-brand-500, #4f46e5)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    </svg>
  );
}

export function FileTextIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--color-brand-500, #4f46e5)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}
